// SPDX-License-Identifier: Apache-2.0

// Workspace registry sync — pushes the SPA's WorkspacesManager state to the
// MCP server's `~/.tether-brain/workspaces.json` so the server can resolve
// `X-Workspace-Id` headers to the right vaultPath.
//
// One subscription per process: registered on first call to
// `startWorkspaceRegistrySync()`. Every workspace mutation (create / rename /
// delete / setFolderPath) emits a change event from the manager; we POST the
// full registry to `/api/workspaces/sync` (idempotent on the server).
//
// Best-effort: a failed sync logs to console.warn and doesn't block the UI.
// The next mutation retries automatically. On first mount we ALSO push once
// so the server learns the registry without waiting for a user action.

import { getWorkspacesManager } from "@tether/storage";
import { isMcpReachable } from "./api";

let started = false;
let inFlight: Promise<void> | null = null;

async function pushRegistry(): Promise<void> {
  // No MCP backend on this origin (e.g. deployed Pages preview) — skip the
  // POST so we don't 405-spam the console.
  if (!isMcpReachable()) return;
  const mgr = getWorkspacesManager();
  // Snapshot the registry. Filter to rows with a folderPath set — rows with
  // no path can't be resolved server-side anyway, and including them would
  // waste a row in the server's map.
  const workspaces = mgr.list().map((w) => ({
    id: w.id,
    vaultPath: w.folderPath ?? "",
  }));
  try {
    const res = await fetch("/api/workspaces/sync", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ workspaces }),
    });
    if (!res.ok) {
      console.warn(`[workspace-sync] HTTP ${res.status}`);
    }
  } catch (err) {
    console.warn(`[workspace-sync] ${(err as Error).message}`);
  }
}

/**
 * Idempotent — call from app boot. Subscribes to manager change events and
 * pushes the registry to the MCP server on every mutation. Coalesces bursts
 * via a single in-flight promise.
 */
export function startWorkspaceRegistrySync(): void {
  if (started) return;
  started = true;
  const mgr = getWorkspacesManager();

  const fire = () => {
    if (inFlight) return;
    inFlight = pushRegistry().finally(() => {
      inFlight = null;
    });
  };

  mgr.subscribe(() => fire());
  // Push once on boot — covers the case where the server cold-started after
  // the SPA already had workspaces in IDB.
  void mgr.init().then(() => fire());
}
